import React, { createContext, useContext } from 'react';
import type { CheckoutLanguage } from './checkoutLabels';
import './checkoutPolicyLinks.css';

// Shopify's checkout footer: under the order button, a link to each policy the store has written — refund, shipping,
// privacy, terms — in the checkout's language, each opening the store page Settings → Policies fills in. A store
// that wrote none shows none.

/** One written policy: the store page it is served at, and which words name it. */
type PolicyKey = 'refundPolicy' | 'shippingPolicy' | 'privacyPolicy' | 'termsOfService';
export type PolicyLink = { key: PolicyKey; href: string };

/** The store page each policy is served at, in the order Shopify lists them. */
const POLICY_PAGES: ReadonlyArray<PolicyLink> = [
  { key: 'refundPolicy', href: '/pages/returns' },
  { key: 'shippingPolicy', href: '/pages/shipping' },
  { key: 'privacyPolicy', href: '/pages/privacy' },
  { key: 'termsOfService', href: '/pages/terms' }
];

/** Each policy's name in the languages a checkout is drawn in. */
const NAMES: Readonly<Record<CheckoutLanguage, Record<PolicyKey, string>>> = {
  en: { refundPolicy: 'Refund policy', shippingPolicy: 'Shipping', privacyPolicy: 'Privacy policy', termsOfService: 'Terms of service' },
  fr: { refundPolicy: 'Politique de retour', shippingPolicy: 'Livraison', privacyPolicy: 'Confidentialité', termsOfService: 'Conditions d’utilisation' },
  ar: { refundPolicy: 'سياسة الإرجاع', shippingPolicy: 'التوصيل', privacyPolicy: 'الخصوصية', termsOfService: 'شروط الاستخدام' }
};

/** What the row of links is called for a screen reader. */
const LIST_NAME: Readonly<Record<CheckoutLanguage, string>> = { en: 'Policies', fr: 'Politiques', ar: 'السياسات' };

// Lists the policies a store has written, read off its settings, so a blank one is never linked.
export function writtenPolicies(settings: unknown): PolicyLink[] {
  const policies = (settings as { policies?: Record<string, unknown> } | null | undefined)?.policies ?? {};
  return POLICY_PAGES.filter(({ key }) => typeof policies[key] === 'string' && (policies[key] as string).trim() !== '');
}

/** The written policies the shell read for this visit; none until the store's settings land. */
export const StorePoliciesContext = createContext<readonly PolicyLink[]>([]);

// Draws the links under the order button, or nothing for a store with no policy written.
export const CheckoutPolicyLinks: React.FC<{ language: CheckoutLanguage }> = ({ language }) => {
  const links = useContext(StorePoliciesContext);
  if (links.length === 0) return null;
  return (
    <nav className="checkout-policies" aria-label={LIST_NAME[language]}>
      {links.map(({ key, href }) => <a key={key} href={href} target="_blank" rel="noopener">{NAMES[language][key]}</a>)}
    </nav>
  );
};
